import { useState, useEffect } from 'react';
import { roomsAPI, propertiesAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { ImageUploader } from '../../components/ImageUploader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { CURRENCIES, getCurrencySymbol } from '../../lib/currencies';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { formatCurrency } from '../../lib/utils';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  BedDouble,
  Loader2,
  Users,
  Link2,
  Baby,
  UtensilsCrossed,
  Tag,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const initialFormData = {
  property_id: '',
  name: '',
  description: '',
  total_inventory: 1,
  price_per_night: 100,
  max_occupancy: 2,
  amenities: [],
  image_url: '',
  gallery: [],
  currency: '',
  currency_rates: {},
};

export default function AdminRooms() {
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [filterProperty, setFilterProperty] = useState('all');
  const [amenityInput, setAmenityInput] = useState('');
  const [syncing, setSyncing] = useState(false);

  const fetchData = async () => {
    try {
      const [roomsRes, propsRes] = await Promise.all([
        roomsAPI.list(),
        propertiesAPI.list(),
      ]);
      setRooms(roomsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAmenity = () => {
    if (amenityInput.trim() && !formData.amenities.includes(amenityInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        amenities: [...prev.amenities, amenityInput.trim()],
      }));
      setAmenityInput('');
    }
  };

  const handleRemoveAmenity = (amenity) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.filter((a) => a !== amenity),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedRoom) {
        await roomsAPI.update(selectedRoom.id, formData);
        toast.success('Room updated successfully');
      } else {
        await roomsAPI.create(formData);
        toast.success('Room created successfully');
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setSelectedRoom(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save room');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (room) => {
    setSelectedRoom(room);
    setFormData({
      property_id: room.property_id,
      name: room.name,
      description: room.description || '',
      total_inventory: room.total_inventory,
      price_per_night: room.price_per_night,
      max_occupancy: room.max_occupancy,
      amenities: room.amenities || [],
      image_url: room.image_url || '',
      gallery: room.gallery || [],
      currency: room.currency || '',
      currency_rates: room.currency_rates || {},
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedRoom) return;

    try {
      await roomsAPI.delete(selectedRoom.id);
      toast.success('Room deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedRoom(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  const openCreateDialog = () => {
    setSelectedRoom(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleSyncAllRooms = async () => {
    const channexProperties = properties.filter(
      (p) => p.channex_property_id && p.channex_property_id.length > 0
    );
    if (channexProperties.length === 0) {
      toast.info('No properties have Channex configured');
      return;
    }
    setSyncing(true);
    let totalCreated = 0;
    let totalUpdated = 0;
    let errors = 0;
    for (const prop of channexProperties) {
      try {
        const res = await propertiesAPI.syncChannex(prop.id);
        totalCreated += res.data.created || 0;
        totalUpdated += res.data.updated || 0;
        errors += res.data.errors || 0;
      } catch {
        errors++;
      }
    }
    setSyncing(false);
    fetchData();
    if (errors > 0) {
      toast.warning(`Sync done with issues`, {
        description: `${totalCreated} created, ${totalUpdated} updated, ${errors} error(s)`
      });
    } else if (totalCreated === 0 && totalUpdated === 0) {
      toast.info('No new rooms to sync');
    } else {
      toast.success(`Rooms synced from Channex`, {
        description: `${totalCreated} created, ${totalUpdated} updated`
      });
    }
  };

  const filteredRooms =
    filterProperty === 'all'
      ? rooms
      : rooms.filter((room) => room.property_id === filterProperty);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-wrapper" data-testid="rooms-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Rooms</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage room types and pricing.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger className="w-[200px]" data-testid="filter-property">
                <SelectValue placeholder="Filter by property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={handleSyncAllRooms}
              disabled={syncing}
              data-testid="sync-rooms-btn"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {syncing ? 'Syncing...' : 'Sync Rooms'}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} data-testid="add-room-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Room
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold">
                    {selectedRoom ? 'Edit Room Type' : 'Add New Room Type'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="property_id">Property *</Label>
                    <Select
                      value={formData.property_id}
                      onValueChange={(value) => handleSelectChange('property_id', value)}
                      required
                    >
                      <SelectTrigger data-testid="room-property-select">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map((prop) => (
                          <SelectItem key={prop.id} value={prop.id}>
                            {prop.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Room Type Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Deluxe Double Room"
                      required
                      data-testid="room-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="A spacious room with city views..."
                      rows={3}
                      data-testid="room-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="total_inventory">Total Inventory *</Label>
                      <Input
                        id="total_inventory"
                        name="total_inventory"
                        type="number"
                        min="1"
                        value={formData.total_inventory}
                        onChange={handleInputChange}
                        required
                        data-testid="room-inventory-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price_per_night">Default Price per Night *</Label>
                      <Input
                        id="price_per_night"
                        name="price_per_night"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price_per_night}
                        onChange={handleInputChange}
                        required
                        data-testid="room-price-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max_occupancy">Max Occupancy *</Label>
                      <Input
                        id="max_occupancy"
                        name="max_occupancy"
                        type="number"
                        min="1"
                        value={formData.max_occupancy}
                        onChange={handleInputChange}
                        required
                        data-testid="room-occupancy-input"
                      />
                    </div>
                  </div>

                  {/* Default Currency */}
                  <div className="space-y-2">
                    <Label htmlFor="currency">Default Currency</Label>
                    <Select
                      value={formData.currency || ''}
                      onValueChange={(val) => setFormData((prev) => ({ ...prev, currency: val }))}
                    >
                      <SelectTrigger data-testid="room-currency-select">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.code} — {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Multi-Currency Rates */}
                  <div className="space-y-3" data-testid="currency-rates-section">
                    <Label>Additional Currency Rates</Label>
                    <p className="text-xs text-muted-foreground -mt-1">
                      Set prices in other currencies for this room. The default price above uses the selected currency.
                    </p>
                    {Object.entries(formData.currency_rates || {}).map(([code, rate]) => {
                      const curr = CURRENCIES.find((c) => c.code === code);
                      return (
                        <div key={code} className="flex items-center gap-2" data-testid={`rate-row-${code}`}>
                          <Badge variant="outline" className="min-w-[80px] justify-center font-mono">
                            {curr?.symbol || ''} {code}
                          </Badge>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={rate}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currency_rates: {
                                  ...prev.currency_rates,
                                  [code]: parseFloat(e.target.value) || 0,
                                },
                              }))
                            }
                            className="flex-1"
                            data-testid={`rate-input-${code}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() =>
                              setFormData((prev) => {
                                const updated = { ...prev.currency_rates };
                                delete updated[code];
                                return { ...prev, currency_rates: updated };
                              })
                            }
                            data-testid={`rate-remove-${code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-2">
                      <Select
                        value=""
                        onValueChange={(code) => {
                          if (code && !formData.currency_rates?.[code]) {
                            setFormData((prev) => ({
                              ...prev,
                              currency_rates: { ...prev.currency_rates, [code]: 0 },
                            }));
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1" data-testid="add-currency-rate-select">
                          <SelectValue placeholder="+ Add rate in another currency" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {CURRENCIES.filter(
                            (c) =>
                              c.code !== formData.currency &&
                              !formData.currency_rates?.[c.code]
                          ).map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.symbol} {c.code} — {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Room Gallery</Label>
                    <ImageUploader
                      images={formData.gallery}
                      onChange={(imgs) => setFormData((prev) => ({ ...prev, gallery: imgs }))}
                      maxImages={10}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Amenities</Label>
                    <div className="flex gap-2">
                      <Input
                        value={amenityInput}
                        onChange={(e) => setAmenityInput(e.target.value)}
                        placeholder="WiFi, AC, Mini Bar..."
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAmenity())}
                        data-testid="amenity-input"
                      />
                      <Button type="button" variant="outline" onClick={handleAddAmenity}>
                        Add
                      </Button>
                    </div>
                    {formData.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.amenities.map((amenity) => (
                          <Badge
                            key={amenity}
                            variant="secondary"
                            className="cursor-pointer hover:bg-destructive/10"
                            onClick={() => handleRemoveAmenity(amenity)}
                          >
                            {amenity} ×
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Channex Data Panel (read-only, shown when room is synced from Channex) */}
                  {selectedRoom?.channex_room_type_id && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 space-y-3" data-testid="channex-data-panel">
                      <div className="flex items-center gap-2 mb-1">
                        <Link2 className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-800">Synced from Channex</span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground block text-xs">Channex Room ID</span>
                          <span className="font-mono text-xs break-all">{selectedRoom.channex_room_type_id}</span>
                        </div>
                        {selectedRoom.channex_rate_plan_id && (
                          <div>
                            <span className="text-muted-foreground block text-xs">Rate Plan ID</span>
                            <span className="font-mono text-xs break-all">{selectedRoom.channex_rate_plan_id}</span>
                          </div>
                        )}
                        {selectedRoom.channex_data?.rate_plan_title && (
                          <div>
                            <span className="text-muted-foreground block text-xs">Rate Plan</span>
                            <span className="font-medium">{selectedRoom.channex_data.rate_plan_title}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground block text-xs">Room Kind</span>
                          <span className="font-medium capitalize">{selectedRoom.channex_data?.room_kind || '—'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-xs">Sell Mode</span>
                          <span className="font-medium">{selectedRoom.channex_data?.sell_mode?.replace('_', ' ') || '—'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-xs">Meal Type</span>
                          <span className="font-medium capitalize">{selectedRoom.channex_data?.meal_type?.replace(/_/g, ' ') || 'None'}</span>
                        </div>
                      </div>

                      <div className="border-t border-blue-200 pt-3">
                        <span className="text-xs text-muted-foreground block mb-2">Occupancy Breakdown</span>
                        <div className="flex flex-wrap gap-3">
                          <div className="flex items-center gap-1.5 text-sm">
                            <Users className="h-3.5 w-3.5 text-blue-600" />
                            <span><strong>{selectedRoom.channex_data?.occ_adults ?? '—'}</strong> Adults</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm">
                            <Baby className="h-3.5 w-3.5 text-blue-600" />
                            <span><strong>{selectedRoom.channex_data?.occ_children ?? '—'}</strong> Children</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm">
                            <Baby className="h-3.5 w-3.5 text-blue-600" />
                            <span><strong>{selectedRoom.channex_data?.occ_infants ?? '—'}</strong> Infants</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm">
                            <Tag className="h-3.5 w-3.5 text-blue-600" />
                            <span>Default: <strong>{selectedRoom.channex_data?.default_occupancy ?? '—'}</strong></span>
                          </div>
                          {selectedRoom.channex_data?.capacity && (
                            <div className="flex items-center gap-1.5 text-sm">
                              <BedDouble className="h-3.5 w-3.5 text-blue-600" />
                              <span>Beds: <strong>{selectedRoom.channex_data.capacity}</strong></span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="save-room-btn">
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {selectedRoom ? 'Update' : 'Create'} Room
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Rooms table */}
        {filteredRooms.length === 0 ? (
          <div className="bg-white rounded-lg border border-border p-12 text-center">
            <BedDouble className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No rooms yet</h3>
            <p className="text-muted-foreground mb-4">
              {properties.length === 0
                ? 'Add a property first, then add room types.'
                : 'Add room types to start accepting bookings.'}
            </p>
            {properties.length > 0 && (
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Room
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Room Type</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Inventory</TableHead>
                  <TableHead>Price/Night</TableHead>
                  <TableHead>Max Guests</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRooms.map((room) => (
                  <TableRow key={room.id} data-testid={`room-row-${room.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                          {room.image_url ? (
                            <img
                              src={room.image_url}
                              alt={room.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BedDouble className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{room.name}</p>
                          <div className="flex items-center gap-1.5">
                            {room.channex_room_type_id && (
                              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 px-1.5 py-0">
                                Channex
                              </Badge>
                            )}
                            {room.amenities?.length > 0 && (
                              <span className="text-sm text-muted-foreground">
                                {room.amenities.slice(0, 3).join(', ')}
                                {room.amenities.length > 3 && ` +${room.amenities.length - 3}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{room.property_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{room.total_inventory} rooms</Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-1 font-medium">
                          <span className="text-muted-foreground text-sm">{getCurrencySymbol(room.currency || 'USD')}</span>
                          {room.price_per_night}
                          <span className="text-xs text-muted-foreground">{room.currency || 'USD'}</span>
                        </div>
                        {room.currency_rates && Object.keys(room.currency_rates).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {Object.entries(room.currency_rates).map(([code, rate]) => (
                              <span key={code} className="text-xs text-muted-foreground">
                                {getCurrencySymbol(code)}{rate} {code}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {room.max_occupancy}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          room.is_active
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : 'bg-gray-100 text-gray-800 border-gray-200'
                        }
                      >
                        {room.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`room-menu-${room.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(room)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedRoom(room);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Delete confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Room Type</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedRoom?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
